<!--
  NotePopover Component

  Displays a note icon that shows the note details in a popover when clicked.
  The note is fetched from the API on the first open.

  Props:
  - notePk: Number - pk of the note to display (from `note_pk` on the order list)
  - position: String - popover position ('bottom-right', 'bottom-left', 'top-right', 'top-left')
-->
<template>
  <div class="note-popover-container">
    <button
      @click.stop="togglePopover"
      class="btn btn-white btn-xs"
      title="Zawiera notatkę"
    >
      <i class="fa fa-sticky-note" aria-hidden="true"></i>
    </button>

    <div
      v-if="showPopover"
      @click.stop
      class="note-popover"
      :style="popoverStyle"
    >
      <div class="note-popover-header">
        <h5>Szczegóły notatki</h5>
        <button @click.stop="closePopover" class="close-btn">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="note-popover-content">
        <div v-if="loading" class="note-loading">
          <i class="fa fa-spinner fa-spin"></i>
        </div>

        <div v-else-if="!note" class="note-loading">Brak danych notatki</div>

        <div v-else class="note-item">
          <div class="note-row">
            <span class="note-label">Autor</span>
            <span class="note-value">{{ note.created_by ?? "-" }}</span>
          </div>

          <div class="note-row">
            <span class="note-label">Treść</span>
            <span class="note-value" v-html="note.text"></span>
          </div>

          <div v-if="note.tags?.length" class="note-row">
            <span class="note-label">Tagi</span>
            <span class="note-value">
              <span
                v-for="(tag, index) in note.tags"
                :key="index"
                class="badge bg-secondary"
              >
                {{ tag.name }}
              </span>
            </span>
          </div>

          <div class="note-row">
            <span class="note-label">Data utworzenia</span>
            <span class="note-value">{{ note.date_added }}</span>
          </div>

          <div class="note-row">
            <span class="note-label">Data modyfikacji</span>
            <span class="note-value">
              {{ note.date_modified }}
              <template v-if="note.last_modified_by">
                ({{ note.last_modified_by }})
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

// Global state for managing open popovers
const openPopoverId = ref(null);

export default {
  name: "NotePopover",
  props: {
    notePk: {
      type: Number,
      required: true,
    },
    position: {
      type: String,
      default: "bottom-right",
    },
  },
  setup(props) {
    const store = useStore();
    const popoverId = ref(Math.random().toString(36).substr(2, 9));
    const note = ref(null);
    const loading = ref(false);

    const noteDetailData = computed(() => store.getters.getNoteDetailData);

    const showPopover = computed({
      get: () => openPopoverId.value === popoverId.value,
      set: (value) => {
        if (value) {
          openPopoverId.value = popoverId.value;
        } else {
          openPopoverId.value = null;
        }
      },
    });

    const popoverStyle = computed(() => {
      if (!showPopover.value) return {};

      const positions = {
        "bottom-right": {
          top: "100%",
          left: "0",
          right: "auto",
        },
        "bottom-left": {
          top: "100%",
          right: "0",
          left: "auto",
        },
        "top-right": {
          bottom: "100%",
          left: "0",
          right: "auto",
        },
        "top-left": {
          bottom: "100%",
          right: "0",
          left: "auto",
        },
      };

      return {
        ...(positions[props.position] || positions["bottom-right"]),
        position: "absolute",
        zIndex: 9999,
      };
    });

    watch(
      () => noteDetailData.value,
      function (newValue) {
        // popovery współdzielą stan w store, bierzemy tylko odpowiedź dla swojej notatki
        if (newValue?.data?.pk !== props.notePk) return;

        note.value = newValue.data;
        loading.value = false;
      }
    );

    const togglePopover = () => {
      if (showPopover.value) {
        showPopover.value = false;
        return;
      }

      showPopover.value = true;

      if (!note.value) {
        loading.value = true;
        store.dispatch("getNote", props.notePk);
      }
    };

    const closePopover = () => {
      showPopover.value = false;
    };

    return {
      note,
      loading,
      showPopover,
      popoverStyle,
      togglePopover,
      closePopover,
    };
  },
};
</script>

<style scoped>
/* Bootstrap 5 nie ma już .btn-xs - odtworzone jak w panelu AngularJS (Bootstrap 3) */
.btn-xs {
  padding: 1px 5px;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 3px;
}

.note-popover-container {
  position: relative;
  display: inline-block;
}

.note-popover {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 420px;
  max-height: 400px;
  overflow: hidden;
  text-align: left;
  white-space: normal;
}

.note-popover-header {
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.note-popover-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
}

.close-btn:hover {
  color: #333;
}

.note-popover-content {
  max-height: 320px;
  overflow-y: auto;
}

.note-loading {
  padding: 20px;
  text-align: center;
  color: #666;
}

.note-item {
  padding: 12px 16px;
}

.note-row {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
}

.note-row:last-child {
  border-bottom: none;
}

.note-label {
  color: #666;
  flex: 0 0 110px;
  font-weight: 600;
}

.note-value {
  color: #333;
  flex: 1 1 auto;
  word-break: break-word;
}
</style>
